/**
 * useTableSort Hook
 * 
 * Reusable hook for managing table column sorting state.
 * Implements industry-standard 3-click sorting cycle:
 * - First click: Ascending (A→Z / 0→9)
 * - Second click: Descending (Z→A / 9→0)
 * - Third click: Reset to default order
 * 
 * @example
 * const { sortConfig, handleSort, getSortParams, resetSort } = useTableSort({
 *   defaultSortKey: 'created_at',
 *   defaultSortOrder: 'desc'
 * });
 */
import { useState, useCallback, useMemo } from 'react';

/**
 * Sort directions enum
 */
export const SORT_DIRECTION = {
  ASC: 'asc',
  DESC: 'desc',
  NONE: null
};

/**
 * Get next sort direction in the cycle: null -> asc -> desc -> null
 */
const getNextSortDirection = (currentDirection) => {
  switch (currentDirection) {
    case SORT_DIRECTION.NONE:
      return SORT_DIRECTION.ASC;
    case SORT_DIRECTION.ASC:
      return SORT_DIRECTION.DESC;
    case SORT_DIRECTION.DESC:
      return SORT_DIRECTION.NONE;
    default:
      return SORT_DIRECTION.ASC;
  }
};

/**
 * Custom hook for table sorting
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.defaultSortKey - Default column to sort by
 * @param {string} options.defaultSortOrder - Default sort direction ('asc' or 'desc')
 * @param {Function} options.onSortChange - Callback when sort changes (for server-side sorting)
 * @returns {Object} Sort state and handlers
 */
export const useTableSort = ({
  defaultSortKey = 'created_at',
  defaultSortOrder = 'desc',
  onSortChange = null
} = {}) => {
  // Sort configuration state
  const [sortConfig, setSortConfig] = useState({
    key: null,        // Currently sorted column (null = default order)
    direction: null   // Current sort direction (null = default order)
  });

  /**
   * Handle column header click - cycles through sort states
   * @param {string} columnKey - The column identifier to sort by
   */
  const handleSort = useCallback((columnKey) => {
    setSortConfig((prevConfig) => {
      let newDirection;
      
      if (prevConfig.key !== columnKey) {
        // Different column clicked - start with ascending
        newDirection = SORT_DIRECTION.ASC;
      } else {
        // Same column clicked - cycle to next direction
        newDirection = getNextSortDirection(prevConfig.direction);
      }
      
      const newConfig = {
        key: newDirection === SORT_DIRECTION.NONE ? null : columnKey,
        direction: newDirection
      };
      
      // Notify parent component if callback provided
      if (onSortChange) {
        onSortChange(newConfig);
      }
      
      return newConfig;
    });
  }, [onSortChange]);

  /**
   * Reset sort to default state
   */
  const resetSort = useCallback(() => {
    const defaultConfig = { key: null, direction: null };
    setSortConfig(defaultConfig);
    if (onSortChange) {
      onSortChange(defaultConfig);
    }
  }, [onSortChange]);

  /**
   * Get API parameters for server-side sorting
   * @returns {Object} { sort_by, sort_order } for API calls
   */
  const getSortParams = useCallback(() => {
    if (sortConfig.key && sortConfig.direction) {
      return {
        sort_by: sortConfig.key,
        sort_order: sortConfig.direction
      };
    }
    // Return default sort when no active sort
    return {
      sort_by: defaultSortKey,
      sort_order: defaultSortOrder
    };
  }, [sortConfig, defaultSortKey, defaultSortOrder]);

  /**
   * Check if a specific column is currently being sorted
   * @param {string} columnKey - Column identifier
   * @returns {boolean}
   */
  const isSorted = useCallback((columnKey) => {
    return sortConfig.key === columnKey && sortConfig.direction !== null;
  }, [sortConfig]);

  /**
   * Get sort direction for a specific column
   * @param {string} columnKey - Column identifier
   * @returns {string|null} 'asc', 'desc', or null
   */
  const getSortDirection = useCallback((columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction;
    }
    return null;
  }, [sortConfig]);

  /**
   * Memoized sort indicator props for a column
   * @param {string} columnKey - Column identifier
   * @returns {Object} { isSorted, direction }
   */
  const getSortIndicatorProps = useCallback((columnKey) => ({
    isSorted: isSorted(columnKey),
    direction: getSortDirection(columnKey)
  }), [isSorted, getSortDirection]);

  return {
    sortConfig,
    handleSort,
    resetSort,
    getSortParams,
    isSorted,
    getSortDirection,
    getSortIndicatorProps
  };
};

/**
 * Client-side sorting function for arrays
 * Use this when data is already loaded and you want to sort in-memory
 * 
 * @param {Array} data - Array of objects to sort
 * @param {string} key - Property key to sort by
 * @param {string} direction - 'asc' or 'desc'
 * @returns {Array} Sorted array (new reference)
 */
export const sortData = (data, key, direction) => {
  if (!key || !direction || !Array.isArray(data)) {
    return data;
  }

  return [...data].sort((a, b) => {
    let aVal = a[key];
    let bVal = b[key];

    // Handle null/undefined values
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return direction === 'asc' ? 1 : -1;
    if (bVal == null) return direction === 'asc' ? -1 : 1;

    // Handle string comparison (case-insensitive)
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    // Handle date strings
    if (key.includes('date') || key.includes('_at') || key.includes('Date')) {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }

    // Compare
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

export default useTableSort;
